import {
  PermissionCode,
  onboardingTemplateDefinitionSchema,
  type OnboardingCaseDetail,
  type OnboardingCaseTask,
  type OnboardingTemplateDetail,
  type OnboardingTemplatePhase,
} from '@gb-ticket/shared-types';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { formatDate, formatDateTime } from '@/lib/format';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import {
  onboardingPanelClassName,
  onboardingTableFrameClassName,
} from '@/products/onboarding/features/onboarding-panel';
import { OnboardingSystemActionFields } from '@/products/onboarding/features/onboarding-system-action-fields';
import { OnboardingTaskFiles } from '@/products/onboarding/features/onboarding-task-files';
import { OnboardingTaskFields } from '@/products/onboarding/features/onboarding-task-fields';
import {
  ONBOARDING_ASSIGNEE_KIND_LABEL,
  ONBOARDING_CASE_EVENT_LABEL,
  onboardingCaseStateLabel,
  ONBOARDING_TASK_KIND_LABEL,
  ONBOARDING_TASK_STATUS_LABEL,
  onboardingCaseStage,
  onboardingCaseSubjectName,
  onboardingNamedOrDash,
} from '@/products/onboarding/features/onboarding-template-labels';
import {
  OnboardingFlowCanvas,
  type FlowSelection,
} from '@/products/onboarding/features/onboarding-flow-canvas';
import {
  flowToDefinition,
  materializeLayout,
  type FlowPhase,
} from '@/products/onboarding/features/onboarding-flow-layout';
import { TaskEditor } from '@/products/onboarding/pages/onboarding-template-detail-page';
import {
  onboardingCaseKey,
  useAdmitOnboardingCase,
  useCancelOnboardingCase,
  useCancelOnboardingPhaseTasks,
  useCompleteOnboardingCase,
  useOnboardingCase,
  usePatchOnboardingCaseDates,
  useReopenOnboardingCase,
  useStartOnboardingCase,
} from '@/products/onboarding/features/use-onboarding-cases';
import { onboardingFieldSubmission } from '@/products/onboarding/features/onboarding-task-field-utils';
import { useOnboardingAssigneeOptions } from '@/products/onboarding/features/use-onboarding-assignee-options';
import {
  useCancelOnboardingTask,
  useCompleteOnboardingTask,
  useNotApplicableOnboardingTask,
  useReassignOnboardingTask,
  useReopenOnboardingTask,
  useSkipOnboardingTask,
} from '@/products/onboarding/features/use-onboarding-tasks';
import { ONBOARDING_CASES_PATH } from '@/products/onboarding/onboarding-paths';
import {
  useOnboardingTemplate,
  useUpdateOnboardingTemplate,
} from '@/products/onboarding/features/use-onboarding-templates';

export function OnboardingCaseDetailPage() {
  const copy = useVoiceCopy();
  const { id = '' } = useParams();
  const me = useMe();
  const detailQuery = useOnboardingCase(id);
  const detail = detailQuery.data;
  const subjectName = detail ? onboardingCaseSubjectName(detail) : 'Vorgang';
  usePageTitle(detail ? `${detail.number} · ${subjectName}` : 'Vorgang');

  const start = useStartOnboardingCase();
  const admit = useAdmitOnboardingCase();
  const completeCase = useCompleteOnboardingCase();
  const cancel = useCancelOnboardingCase();
  const reopen = useReopenOnboardingCase();
  const patchDates = usePatchOnboardingCaseDates();
  const completeTask = useCompleteOnboardingTask();
  const skipTask = useSkipOnboardingTask();
  const cancelTask = useCancelOnboardingTask();
  const cancelPhase = useCancelOnboardingPhaseTasks();
  const notApplicableTask = useNotApplicableOnboardingTask();
  const reassignTask = useReassignOnboardingTask();
  const reopenTask = useReopenOnboardingTask();
  const client = useQueryClient();
  const canEditTemplate = hasPermission(me.data, PermissionCode.ONBOARDING_TEMPLATES_MANAGE);
  const templateEditor = useOnboardingTemplate(detail?.templateId ?? '', {
    enabled: canEditTemplate && Boolean(detail?.templateId),
  });
  const updateTemplate = useUpdateOnboardingTemplate(detail?.templateId ?? '');
  const [flowStamp, setFlowStamp] = useState<string | null>(null);
  const [phases, setPhases] = useState<FlowPhase[]>([]);
  const [selection, setSelection] = useState<FlowSelection>(null);
  const [flowError, setFlowError] = useState<string | null>(null);
  const [flowDirty, setFlowDirty] = useState(false);
  const editorDefinition = canEditTemplate
    ? templateEditorDefinition(templateEditor.data)
    : (detail?.flow ?? null);
  const editorReady = !canEditTemplate || templateEditor.isSuccess;
  const editorStamp = editorReady && editorDefinition ? JSON.stringify(editorDefinition) : null;
  if (editorStamp !== null && editorDefinition && editorStamp !== flowStamp) {
    setFlowStamp(editorStamp);
    setFlowDirty(false);
    setPhases(materializeLayout(editorDefinition));
  }

  const [cancelOpen, setCancelOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [datesOpen, setDatesOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [reopenReason, setReopenReason] = useState('');
  const [entryOn, setEntryOn] = useState('');
  const [exitOn, setExitOn] = useState('');
  const [lastWorkingDay, setLastWorkingDay] = useState('');
  const [admitOpen, setAdmitOpen] = useState(false);
  const [accountKind, setAccountKind] = useState<'entra' | 'local'>('entra');

  if (detailQuery.isError && isForbidden(detailQuery.error)) {
    return <p className="text-sm text-muted-foreground">{copy('onboarding.access.denied')}</p>;
  }

  if (detailQuery.isPending) {
    return <p className="text-sm text-muted-foreground">Vorgang wird geladen …</p>;
  }

  if (detailQuery.isError || !detail) {
    return (
      <p className="text-sm text-destructive">
        {detailQuery.error instanceof ApiRequestError
          ? detailQuery.error.message
          : 'Der Vorgang konnte nicht geladen werden.'}
      </p>
    );
  }

  const loaded = detail;

  const canManage = hasPermission(me.data, PermissionCode.ONBOARDING_CASES_MANAGE);
  const canReopen = hasPermission(me.data, PermissionCode.ONBOARDING_CASES_REOPEN);
  const isOwner = me.data?.id === detail.ownerUserId;
  const stage = onboardingCaseStage(detail);
  const busy =
    start.isPending ||
    admit.isPending ||
    completeCase.isPending ||
    cancel.isPending ||
    reopen.isPending ||
    patchDates.isPending ||
    completeTask.isPending ||
    skipTask.isPending ||
    cancelTask.isPending ||
    cancelPhase.isPending ||
    notApplicableTask.isPending ||
    reassignTask.isPending ||
    reopenTask.isPending;

  async function runAction(
    action: () => Promise<unknown>,
    success: string,
    fallback: string,
  ): Promise<boolean> {
    try {
      await action();
      toast.success(success);
      return true;
    } catch (caught) {
      toast.error(caught instanceof ApiRequestError ? caught.message : fallback);
      return false;
    }
  }

  async function saveFlow() {
    if (!loaded.templateId || !canEditTemplate || !templateEditor.isSuccess || !editorDefinition) {
      return;
    }
    const parsed = onboardingTemplateDefinitionSchema.safeParse(flowToDefinition(phases));
    if (!parsed.success) {
      setFlowError(parsed.error.issues[0]?.message ?? 'Die Definition ist ungültig.');
      return;
    }
    try {
      await updateTemplate.mutateAsync({ definition: parsed.data });
      await client.invalidateQueries({ queryKey: onboardingCaseKey(loaded.id) });
      setFlowDirty(false);
      setFlowError(null);
      toast.success('Entwurf gespeichert. Dieser Vorgang bleibt unverändert.');
    } catch (caught) {
      setFlowError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Der Entwurf konnte nicht gespeichert werden.',
      );
    }
  }

  const statuses = Object.fromEntries(detail.tasks.map((task) => [task.key, task.status]));
  const savedFlowKeys = new Set(
    (detail.flow?.phases ?? []).flatMap((phase) => phase.tasks.map((task) => task.key)),
  );
  const orphans = detail.tasks.filter((task) => !savedFlowKeys.has(task.key));
  const listedTasks = taskRows(detail.tasks.filter((task) => savedFlowKeys.has(task.key)));
  const selectedTask =
    selection?.type === 'task'
      ? phases.flatMap((phase) => phase.tasks).find((task) => task.key === selection.key)
      : undefined;
  const selectedCaseTask =
    selection?.type === 'task'
      ? detail.tasks.find((task) => task.key === selection.key)
      : undefined;

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title={`${detail.number} · ${subjectName}`}
        description={caseDescription(detail)}
        action={
          <Button type="button" variant="outline" asChild>
            <Link to={ONBOARDING_CASES_PATH}>Zur Liste</Link>
          </Button>
        }
      />

      <div className="flex flex-wrap gap-2">
        {detail.status === 'draft' && canManage ? (
          <Button
            type="button"
            disabled={busy}
            onClick={() =>
              void runAction(
                () => start.mutateAsync({ id: detail.id, body: { revision: detail.revision } }),
                'Vorgang gestartet.',
                'Der Vorgang konnte nicht gestartet werden.',
              )
            }
          >
            Starten
          </Button>
        ) : null}
        {detail.status === 'active' && stage === 'preboarding' && canManage ? (
          <Button
            type="button"
            disabled={busy}
            onClick={() => {
              setAccountKind('entra');
              setAdmitOpen(true);
            }}
          >
            Aufnehmen
          </Button>
        ) : null}
        {detail.status === 'active' && stage !== 'preboarding' && (canManage || isOwner) ? (
          <Button
            type="button"
            disabled={busy}
            onClick={() =>
              void runAction(
                () =>
                  completeCase.mutateAsync({
                    id: detail.id,
                    body: { revision: detail.revision },
                  }),
                'Vorgang abgeschlossen.',
                'Der Vorgang konnte nicht abgeschlossen werden.',
              )
            }
          >
            Abschließen
          </Button>
        ) : null}
        {detail.status === 'active' && canManage ? (
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => {
              setEntryOn(detail.entryOn ?? '');
              setExitOn(detail.exitOn ?? '');
              setLastWorkingDay(detail.lastWorkingDay ?? '');
              setDatesOpen(true);
            }}
          >
            Daten ändern
          </Button>
        ) : null}
        {(detail.status === 'draft' || detail.status === 'active') && canManage ? (
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => {
              setCancelReason('');
              setCancelOpen(true);
            }}
          >
            Vorgang abbrechen
          </Button>
        ) : null}
        {(detail.status === 'completed' || detail.status === 'cancelled') && canReopen ? (
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => {
              setReopenReason('');
              setReopenOpen(true);
            }}
          >
            Wieder öffnen
          </Button>
        ) : null}
      </div>

      {loaded.tasks.length > 0 ? (
        <p className="text-sm text-muted-foreground">{taskProgressLabel(loaded.tasks)}</p>
      ) : null}

      {listedTasks.length > 0 ? (
        <section className="space-y-2">
          <h2 className="text-base font-semibold">Aufgaben</h2>
          <ul className={`${onboardingTableFrameClassName} divide-y`}>
            {listedTasks.map((task) => {
              const selected = selection?.type === 'task' && selection.key === task.key;
              return (
                <li key={task.id}>
                  <button
                    type="button"
                    className={`flex min-h-11 w-full items-center justify-between gap-3 px-4 py-2 text-left hover:bg-muted/60 ${selected ? 'bg-muted' : ''}`}
                    aria-current={selected ? 'true' : undefined}
                    onClick={() => setSelection({ type: 'task', key: task.key })}
                  >
                    <span className="min-w-0">
                      <strong className="block font-medium">{task.title}</strong>
                      <span className="block text-sm text-muted-foreground">
                        {task.phaseTitle}
                        {task.dueOn ? ` · Fällig ${formatDate(task.dueOn)}` : ''}
                        {task.blocked ? ' · Blockiert' : ''}
                      </span>
                    </span>
                    <span className="shrink-0 text-sm text-muted-foreground">
                      {ONBOARDING_TASK_STATUS_LABEL[task.status]}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {selection?.type === 'phase' && canManage ? (
        <PhaseOpenTasksPanel
          phaseTitle={
            detail.tasks.find((task) => task.phaseId === selection.id)?.phaseTitle ?? 'Phase'
          }
          tasks={detail.tasks.filter((task) => task.phaseId === selection.id)}
          busy={busy}
          onCancel={async (reason) =>
            runAction(
              () =>
                cancelPhase.mutateAsync({
                  id: detail.id,
                  phaseId: selection.id,
                  body: { revision: detail.revision, reason },
                }),
              'Offene Aufgaben der Phase storniert.',
              'Die Phase konnte nicht storniert werden.',
            )
          }
        />
      ) : null}

      {flowError ? (
        <p className="text-sm text-destructive" role="alert">
          {flowError}
        </p>
      ) : null}
      {canEditTemplate && flowDirty ? (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Speichern schreibt den geladenen Vorlagenentwurf. Die Aufgaben dieses Vorgangs bleiben
            seine eigene Kopie.
          </p>
          <Button type="button" onClick={() => void saveFlow()} disabled={updateTemplate.isPending}>
            Entwurf speichern
          </Button>
        </div>
      ) : null}

      {detail.flow === null ? (
        <p className="text-sm text-muted-foreground">Diese Vorlage hat keine Aufgaben.</p>
      ) : (
        <div
          className={
            selectedCaseTask || (canEditTemplate && selectedTask)
              ? 'grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]'
              : 'grid items-start gap-4'
          }
        >
          <OnboardingFlowCanvas
            phases={phases}
            editable={canEditTemplate}
            statuses={statuses}
            onPhasesChange={(next) => {
              setPhases(next);
              setFlowDirty(true);
            }}
            onSelect={setSelection}
            onReject={setFlowError}
          />
          {selectedCaseTask || (canEditTemplate && selectedTask) ? (
            <aside className="space-y-3 lg:sticky lg:top-20 lg:max-h-[calc(100dvh-6rem)] lg:overflow-y-auto">
              {selectedCaseTask ? (
                <CaseTaskCard
                  task={selectedCaseTask}
                  fields={taskFields(detail, selectedCaseTask.key)}
                  canManage={canManage}
                  revision={detail.revision}
                  caseActive={detail.status === 'active'}
                  busy={busy}
                  onReopen={() =>
                    runAction(
                      () =>
                        reopenTask.mutateAsync({
                          taskId: selectedCaseTask.id,
                          body: { revision: detail.revision },
                        }),
                      'Aufgabe wieder geöffnet.',
                      'Die Aufgabe konnte nicht wieder geöffnet werden.',
                    )
                  }
                  onSkip={async (reason) =>
                    runAction(
                      () =>
                        skipTask.mutateAsync({
                          taskId: selectedCaseTask.id,
                          body: { revision: detail.revision, reason },
                        }),
                      'Aufgabe übersprungen.',
                      'Die Aufgabe konnte nicht übersprungen werden.',
                    )
                  }
                  onCancel={async (reason) =>
                    runAction(
                      () =>
                        cancelTask.mutateAsync({
                          taskId: selectedCaseTask.id,
                          body: { revision: detail.revision, reason },
                        }),
                      'Aufgabe storniert. Die Aufgabe gilt nicht als erledigt.',
                      'Die Aufgabe konnte nicht storniert werden.',
                    )
                  }
                  onNotApplicable={async (reason) =>
                    runAction(
                      () =>
                        notApplicableTask.mutateAsync({
                          taskId: selectedCaseTask.id,
                          body: { revision: detail.revision, reason },
                        }),
                      'Aufgabe als nicht zutreffend markiert.',
                      'Die Aufgabe konnte nicht geändert werden.',
                    )
                  }
                  onReassign={async (body) =>
                    runAction(
                      () =>
                        reassignTask.mutateAsync({
                          taskId: selectedCaseTask.id,
                          body: { revision: detail.revision, ...body },
                        }),
                      'Aufgabe neu zugewiesen.',
                      'Die Aufgabe konnte nicht zugewiesen werden.',
                    )
                  }
                  onComplete={async (result) => {
                    await runAction(
                      () =>
                        completeTask.mutateAsync({
                          taskId: selectedCaseTask.id,
                          body: { revision: detail.revision, result },
                        }),
                      'Aufgabe abgeschlossen.',
                      'Die Aufgabe konnte nicht abgeschlossen werden.',
                    );
                  }}
                />
              ) : null}
              {canEditTemplate && selectedTask ? (
                <TaskEditor
                  task={selectedTask}
                  onChange={(patch) => {
                    setPhases((current) =>
                      current.map((phase) => ({
                        ...phase,
                        tasks: phase.tasks.map((item) =>
                          item.key === selectedTask.key ? { ...item, ...patch } : item,
                        ),
                      })),
                    );
                    setFlowDirty(true);
                    if (patch.key && patch.key !== selectedTask.key && selection?.type === 'task') {
                      setSelection({ type: 'task', key: patch.key });
                    }
                  }}
                  onRemove={undefined}
                />
              ) : null}
            </aside>
          ) : null}
        </div>
      )}

      {orphans.length > 0 ? (
        <section className="space-y-3">
          <h2 className="text-base font-semibold">Nur in diesem Vorgang</h2>
          {orphans.map((task) => (
            <CaseTaskCard
              key={task.id}
              task={task}
              fields={taskFields(detail, task.key)}
              canManage={canManage}
              revision={detail.revision}
              caseActive={detail.status === 'active'}
              busy={busy}
              onReopen={() =>
                runAction(
                  () =>
                    reopenTask.mutateAsync({
                      taskId: task.id,
                      body: { revision: detail.revision },
                    }),
                  'Aufgabe wieder geöffnet.',
                  'Die Aufgabe konnte nicht wieder geöffnet werden.',
                )
              }
              onSkip={async (reason) =>
                runAction(
                  () =>
                    skipTask.mutateAsync({
                      taskId: task.id,
                      body: { revision: detail.revision, reason },
                    }),
                  'Aufgabe übersprungen.',
                  'Die Aufgabe konnte nicht übersprungen werden.',
                )
              }
              onCancel={async (reason) =>
                runAction(
                  () =>
                    cancelTask.mutateAsync({
                      taskId: task.id,
                      body: { revision: detail.revision, reason },
                    }),
                  'Aufgabe storniert. Die Aufgabe gilt nicht als erledigt.',
                  'Die Aufgabe konnte nicht storniert werden.',
                )
              }
              onNotApplicable={async (reason) =>
                runAction(
                  () =>
                    notApplicableTask.mutateAsync({
                      taskId: task.id,
                      body: { revision: detail.revision, reason },
                    }),
                  'Aufgabe als nicht zutreffend markiert.',
                  'Die Aufgabe konnte nicht geändert werden.',
                )
              }
              onReassign={async (body) =>
                runAction(
                  () =>
                    reassignTask.mutateAsync({
                      taskId: task.id,
                      body: { revision: detail.revision, ...body },
                    }),
                  'Aufgabe neu zugewiesen.',
                  'Die Aufgabe konnte nicht zugewiesen werden.',
                )
              }
              onComplete={async (result) => {
                await runAction(
                  () =>
                    completeTask.mutateAsync({
                      taskId: task.id,
                      body: { revision: detail.revision, result },
                    }),
                  'Aufgabe abgeschlossen.',
                  'Die Aufgabe konnte nicht abgeschlossen werden.',
                );
              }}
            />
          ))}
        </section>
      ) : null}

      <section className="space-y-3">
        <h2 className="text-base font-semibold">Ereignisse</h2>
        {detail.events.length === 0 ? (
          <p className={`${onboardingPanelClassName} text-sm text-muted-foreground`}>
            Noch keine Ereignisse.
          </p>
        ) : (
          <ul className={`${onboardingTableFrameClassName} divide-y`}>
            {detail.events.map((event) => (
              <li key={event.id} className="px-4 py-3 text-sm">
                <p className="font-medium">
                  {ONBOARDING_CASE_EVENT_LABEL[event.type] ?? event.type}
                </p>
                <p className="text-muted-foreground">{formatDateTime(event.at)}</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Dialog open={admitOpen} onOpenChange={setAdmitOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Person aufnehmen</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Anmeldung</Label>
            <Select
              value={accountKind}
              onValueChange={(value) => setAccountKind(value as 'entra' | 'local')}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="entra">Microsoft 365, Konto folgt später</SelectItem>
                <SelectItem value="local">Lokales Konto mit Passwort-Link</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              {accountKind === 'entra'
                ? 'Es entsteht ein Platzhalter mit dieser E-Mail. Der Verzeichnisabgleich trägt später die Entra-ID ein.'
                : 'Die Person erhält einen Link, um ein Passwort zu setzen. In der Benutzerverwaltung kann das Passwort zusätzlich gesetzt werden.'}
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setAdmitOpen(false)}>
              Zurück
            </Button>
            <Button
              type="button"
              disabled={busy}
              onClick={() => {
                void runAction(
                  () =>
                    admit.mutateAsync({
                      id: detail.id,
                      body: { revision: detail.revision, accountKind },
                    }),
                  accountKind === 'entra'
                    ? 'Platzhalter für Microsoft 365 angelegt.'
                    : 'Lokales Konto angelegt. Der Passwort-Link wurde angestoßen.',
                  'Die Person konnte nicht aufgenommen werden.',
                ).then((ok) => {
                  if (ok) {
                    setAdmitOpen(false);
                  }
                });
              }}
            >
              Aufnehmen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {detail.number} · {subjectName} abbrechen?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Der Vorgang wird abgebrochen und gilt nicht als abgeschlossen. Offene Aufgaben werden
            storniert. Erledigte Aufgaben und die Historie bleiben. Erinnerungen laufen nicht
            weiter. Bereits ausgeführte Systemaktionen werden nicht rückgängig gemacht.
          </p>
          <div className="space-y-2">
            <Label htmlFor="onboarding-case-cancel-reason">Grund</Label>
            <Textarea
              id="onboarding-case-cancel-reason"
              value={cancelReason}
              onChange={(event) => setCancelReason(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCancelOpen(false)}>
              Zurück
            </Button>
            <Button
              type="button"
              disabled={busy || cancelReason.trim().length === 0}
              onClick={() => {
                void runAction(
                  () =>
                    cancel.mutateAsync({
                      id: detail.id,
                      body: { revision: detail.revision, reason: cancelReason.trim() },
                    }),
                  'Vorgang abgebrochen.',
                  'Der Vorgang konnte nicht abgebrochen werden.',
                ).then((ok) => {
                  if (ok) {
                    setCancelOpen(false);
                  }
                });
              }}
            >
              Vorgang abbrechen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {detail.number} · {subjectName} wieder öffnen?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Der Vorgang wird wieder aktiv. Stornierte und erledigte Aufgaben bleiben in ihrem Status
            und werden nicht neu geöffnet.
          </p>
          <div className="space-y-2">
            <Label htmlFor="onboarding-case-reopen-reason">Grund</Label>
            <Textarea
              id="onboarding-case-reopen-reason"
              value={reopenReason}
              onChange={(event) => setReopenReason(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setReopenOpen(false)}>
              Zurück
            </Button>
            <Button
              type="button"
              disabled={busy || reopenReason.trim().length < 10}
              onClick={() => {
                void runAction(
                  () =>
                    reopen.mutateAsync({
                      id: detail.id,
                      body: { revision: detail.revision, reason: reopenReason.trim() },
                    }),
                  'Vorgang wieder geöffnet.',
                  'Der Vorgang konnte nicht wieder geöffnet werden.',
                ).then((ok) => {
                  if (ok) {
                    setReopenOpen(false);
                  }
                });
              }}
            >
              Wieder öffnen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={datesOpen} onOpenChange={setDatesOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Daten ändern</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {detail.kind === 'onboarding' ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="onboarding-case-date-entry">Eintritt</Label>
                  <Input
                    id="onboarding-case-date-entry"
                    type="date"
                    value={entryOn}
                    onChange={(event) => setEntryOn(event.target.value)}
                  />
                </div>
                {showsLastWorkingDay(detail) ? (
                  <div className="space-y-2">
                    <Label htmlFor="onboarding-case-date-last-entry">Letzter Arbeitstag</Label>
                    <Input
                      id="onboarding-case-date-last-entry"
                      type="date"
                      value={lastWorkingDay}
                      onChange={(event) => setLastWorkingDay(event.target.value)}
                    />
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="onboarding-case-date-exit">Austritt</Label>
                  <Input
                    id="onboarding-case-date-exit"
                    type="date"
                    value={exitOn}
                    onChange={(event) => setExitOn(event.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="onboarding-case-date-last">Letzter Arbeitstag</Label>
                  <Input
                    id="onboarding-case-date-last"
                    type="date"
                    value={lastWorkingDay}
                    onChange={(event) => setLastWorkingDay(event.target.value)}
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDatesOpen(false)}>
              Zurück
            </Button>
            <Button
              type="button"
              disabled={busy}
              onClick={() => {
                void runAction(
                  () =>
                    patchDates.mutateAsync({
                      id: detail.id,
                      body: {
                        revision: detail.revision,
                        ...(detail.kind === 'onboarding'
                          ? { entryOn: entryOn || null, lastWorkingDay: lastWorkingDay || null }
                          : {
                              exitOn: exitOn || null,
                              lastWorkingDay: lastWorkingDay || null,
                            }),
                      },
                    }),
                  'Daten gespeichert.',
                  'Die Daten konnten nicht geändert werden.',
                ).then((ok) => {
                  if (ok) {
                    setDatesOpen(false);
                  }
                });
              }}
            >
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CaseTaskCard({
  task,
  fields,
  canManage,
  revision,
  caseActive,
  busy,
  onComplete,
  onSkip,
  onCancel,
  onNotApplicable,
  onReassign,
  onReopen,
}: {
  task: OnboardingCaseTask;
  fields: OnboardingTemplatePhase['tasks'][number]['dataFields'];
  canManage: boolean;
  revision: number;
  caseActive: boolean;
  busy: boolean;
  onComplete: (result: Record<string, unknown> | undefined) => Promise<void>;
  onSkip: (reason: string) => Promise<boolean>;
  onCancel: (reason: string) => Promise<boolean>;
  onNotApplicable: (reason: string) => Promise<boolean>;
  onReassign: (body: {
    assigneeKind: OnboardingCaseTask['assigneeKind'];
    assigneeUserId?: string | null;
  }) => Promise<boolean>;
  onReopen: () => Promise<boolean>;
}) {
  const isOpen = task.status === 'open';
  const blocked = task.blocked === true;
  const canFinish = task.canComplete === true;
  const equipment =
    task.kind === 'asset_issue' ||
    task.kind === 'asset_return' ||
    task.kind === 'vehicle_assign' ||
    task.kind === 'vehicle_return';
  const needsFields = (task.kind === 'data' || task.kind === 'review') && fields.length > 0;
  const [values, setValues] = useState<Record<string, unknown>>(() => task.result ?? {});
  const [reasonOpen, setReasonOpen] = useState<'skip' | 'na' | 'cancel' | null>(null);
  const [reason, setReason] = useState('');
  const [reassignOpen, setReassignOpen] = useState(false);

  return (
    <article className={`${onboardingPanelClassName} space-y-2`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-medium">{task.title}</p>
          <p className="text-sm text-muted-foreground">
            {ONBOARDING_TASK_KIND_LABEL[task.kind]}
            {` · ${ONBOARDING_ASSIGNEE_KIND_LABEL[task.assigneeKind]}`}
            {task.dueOn ? ` · Fällig ${formatDate(task.dueOn)}` : ''}
            {blocked ? ' · Blockiert' : ''}
          </p>
        </div>
        <p className="text-sm text-muted-foreground">{ONBOARDING_TASK_STATUS_LABEL[task.status]}</p>
      </div>
      {task.instructions ? (
        <p className="text-sm whitespace-pre-wrap">{task.instructions}</p>
      ) : null}
      {needsFields ? (
        <OnboardingTaskFields
          mode="value"
          fields={fields}
          values={isOpen ? values : (task.result ?? {})}
          onChange={isOpen ? setValues : undefined}
          readOnly={!isOpen}
        />
      ) : null}
      {isOpen && equipment && canFinish && task.systemActionStatus !== 'succeeded' ? (
        <OnboardingSystemActionFields taskId={task.id} kind={task.kind} revision={revision} />
      ) : null}
      {task.kind === 'document_upload' || task.kind === 'document_provide' ? (
        <OnboardingTaskFiles taskId={task.id} canUpload={Boolean(isOpen && canFinish)} />
      ) : null}
      <div className="flex flex-wrap gap-2">
        {isOpen &&
        !blocked &&
        canFinish &&
        (!equipment || task.systemActionStatus === 'succeeded') ? (
          <Button
            type="button"
            size="sm"
            disabled={busy}
            onClick={() =>
              void onComplete(needsFields ? onboardingFieldSubmission(fields, values) : undefined)
            }
          >
            Abschließen
          </Button>
        ) : null}
        {isOpen && canManage ? (
          <>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => {
                setReason('');
                setReasonOpen('skip');
              }}
            >
              Überspringen
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="min-h-11"
              disabled={busy}
              onClick={() => {
                setReason('');
                setReasonOpen('cancel');
              }}
            >
              Stornieren
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => {
                setReason('');
                setReasonOpen('na');
              }}
            >
              Nicht zutreffend
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => setReassignOpen(true)}
            >
              Neu zuweisen
            </Button>
          </>
        ) : null}
        {!isOpen && caseActive && canManage ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => void onReopen()}
          >
            Wieder öffnen
          </Button>
        ) : null}
      </div>
      <Dialog open={reasonOpen !== null} onOpenChange={(open) => !open && setReasonOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reasonOpen === 'na'
                ? 'Nicht zutreffend'
                : reasonOpen === 'cancel'
                  ? `„${task.title}“ stornieren?`
                  : 'Aufgabe überspringen'}
            </DialogTitle>
          </DialogHeader>
          {reasonOpen === 'cancel' ? (
            <p className="text-sm text-muted-foreground">
              Die Aufgabe gilt danach nicht als erledigt
              {task.required ? ' und blockiert den Abschluss, bis sie wieder geöffnet wird' : ''}.
              {task.systemActionStatus === 'succeeded'
                ? ' Eine bereits ausgeführte Systemaktion bleibt bestehen.'
                : ''}
            </p>
          ) : null}
          <div className="space-y-2">
            <Label htmlFor={`task-reason-${task.id}`}>Grund</Label>
            <Textarea
              id={`task-reason-${task.id}`}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setReasonOpen(null)}>
              Zurück
            </Button>
            <Button
              type="button"
              disabled={busy || reason.trim().length < 10}
              onClick={() => {
                const next = reasonOpen;
                const action =
                  next === 'na'
                    ? onNotApplicable(reason.trim())
                    : next === 'cancel'
                      ? onCancel(reason.trim())
                      : onSkip(reason.trim());
                void action.then((ok) => {
                  if (ok) {
                    setReasonOpen(null);
                  }
                });
              }}
            >
              {reasonOpen === 'cancel' ? 'Stornieren' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {reassignOpen ? (
        <ReassignTaskDialog
          taskId={task.id}
          busy={busy}
          onClose={() => setReassignOpen(false)}
          onSave={async (userId) => {
            const ok = await onReassign({ assigneeKind: 'user', assigneeUserId: userId });
            if (ok) {
              setReassignOpen(false);
            }
          }}
        />
      ) : null}
    </article>
  );
}

function ReassignTaskDialog({
  taskId,
  busy,
  onClose,
  onSave,
}: {
  taskId: string;
  busy: boolean;
  onClose: () => void;
  onSave: (userId: string) => Promise<void>;
}) {
  const [q, setQ] = useState('');
  const options = useOnboardingAssigneeOptions({
    page: 1,
    pageSize: 50,
    includeInactive: false,
    q: q.trim() || undefined,
  });
  const [userId, setUserId] = useState('');
  const people = options.data?.users.items ?? [];
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Aufgabe neu zuweisen</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor={`reassign-q-${taskId}`}>Suche</Label>
          <Input
            id={`reassign-q-${taskId}`}
            value={q}
            placeholder="Name oder E-Mail"
            onChange={(event) => setQ(event.target.value)}
          />
          <Label htmlFor={`reassign-${taskId}`}>Person</Label>
          <Select
            value={userId || 'none'}
            onValueChange={(value) => value !== 'none' && setUserId(value)}
          >
            <SelectTrigger id={`reassign-${taskId}`} className="w-full">
              <SelectValue placeholder="Person wählen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none" disabled>
                Person wählen
              </SelectItem>
              {people.map((person) => (
                <SelectItem key={person.id} value={person.id}>
                  {person.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Zurück
          </Button>
          <Button
            type="button"
            disabled={busy || userId.length !== 36}
            onClick={() => void onSave(userId)}
          >
            Zuweisen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PhaseOpenTasksPanel({
  phaseTitle,
  tasks,
  busy,
  onCancel,
}: {
  phaseTitle: string;
  tasks: readonly OnboardingCaseTask[];
  busy: boolean;
  onCancel: (reason: string) => Promise<boolean>;
}) {
  const open = tasks.filter((task) => task.status === 'open');
  const kept = tasks.filter((task) => task.status !== 'open');
  const [openDialog, setOpenDialog] = useState(false);
  const [reason, setReason] = useState('');
  return (
    <section className={`${onboardingPanelClassName} space-y-3`}>
      <h2 className="text-base font-semibold">Phase {phaseTitle}</h2>
      <p className="text-sm text-muted-foreground">
        Eine Phase in einem Vorgang wird nicht gelöscht. Offene Aufgaben können storniert werden.
        Erledigte Aufgaben und die Vorlage bleiben.
      </p>
      {open.length > 0 ? (
        <ul className="list-disc space-y-1 pl-5 text-sm">
          {open.map((task) => (
            <li key={task.id}>
              {task.title}
              {task.required ? ' · Pflicht' : ''}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">Keine offene Aufgabe in dieser Phase.</p>
      )}
      {kept.length > 0 ? (
        <p className="text-sm text-muted-foreground">
          Bleibt stehen:{' '}
          {kept
            .map((task) => `${task.title} (${ONBOARDING_TASK_STATUS_LABEL[task.status]})`)
            .join(', ')}
        </p>
      ) : null}
      {open.length > 0 ? (
        <Button
          type="button"
          variant="outline"
          className="min-h-11"
          disabled={busy}
          onClick={() => {
            setReason('');
            setOpenDialog(true);
          }}
        >
          Offene Aufgaben stornieren
        </Button>
      ) : null}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Offene Aufgaben in „{phaseTitle}“ stornieren?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {open.length === 1
              ? '1 offene Aufgabe wird storniert und gilt nicht als erledigt.'
              : `${open.length} offene Aufgaben werden storniert und gelten nicht als erledigt.`}{' '}
            Die Vorlage bleibt unverändert. Pflichtaufgaben blockieren den Abschluss, bis sie wieder
            geöffnet werden.
          </p>
          <div className="space-y-2">
            <Label htmlFor="phase-cancel-reason">Grund</Label>
            <Textarea
              id="phase-cancel-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpenDialog(false)}>
              Zurück
            </Button>
            <Button
              type="button"
              disabled={busy || reason.trim().length < 10}
              onClick={() => {
                void onCancel(reason.trim()).then((ok) => {
                  if (ok) {
                    setOpenDialog(false);
                  }
                });
              }}
            >
              Stornieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function taskRows(tasks: readonly OnboardingCaseTask[]): OnboardingCaseTask[] {
  return [...tasks].sort(
    (left, right) =>
      left.sortOrder - right.sortOrder || left.title.localeCompare(right.title, 'de'),
  );
}

function templateEditorDefinition(template: OnboardingTemplateDetail | undefined) {
  if (!template) {
    return null;
  }
  const draft = template.versions.find((version) => version.id === template.draftVersionId);
  if (draft) {
    return draft.definition;
  }
  return (
    template.versions.find((version) => version.id === template.currentPublishedVersionId)
      ?.definition ?? null
  );
}

function showsLastWorkingDay(detail: OnboardingCaseDetail): boolean {
  if (detail.lastWorkingDay) {
    return true;
  }
  const phases = detail.templateSnapshot?.phases ?? detail.flow?.phases ?? [];
  return phases.some((phase) => phase.tasks.some((task) => task.anchor === 'last_working_day'));
}

function taskProgressLabel(tasks: readonly OnboardingCaseTask[]): string {
  const open = tasks.filter((task) => task.status === 'open');
  const done = tasks.filter((task) => task.status === 'completed');
  const cancelled = tasks.filter((task) => task.status === 'cancelled');
  const openRequired = open.filter((task) => task.required !== false).length;
  return `${open.length} offen, ${done.length} erledigt, ${cancelled.length} storniert von ${tasks.length}. Davon ${openRequired} offene Pflicht.`;
}

function caseDescription(detail: OnboardingCaseDetail): string {
  return [
    onboardingCaseStateLabel(detail),
    detail.entryOn ? `Eintritt ${formatDate(detail.entryOn)}` : null,
    detail.exitOn ? `Austritt ${formatDate(detail.exitOn)}` : null,
    detail.lastWorkingDay ? `Letzter Arbeitstag ${formatDate(detail.lastWorkingDay)}` : null,
    onboardingNamedOrDash(detail.ownerDisplayName),
  ]
    .filter(Boolean)
    .join(' · ');
}

function taskFields(detail: OnboardingCaseDetail, key: string) {
  return (
    detail.templateSnapshot?.phases.flatMap((phase) => phase.tasks).find((task) => task.key === key)
      ?.dataFields ?? []
  );
}

function isForbidden(error: unknown): boolean {
  return error instanceof ApiRequestError && error.error.statusCode === 403;
}
